import React, { useMemo } from "react";
import './Graficas.scss'
import Card from "../../../../../components/Atomsv2/Card/Card";
import {
    PieChart, Pie, Cell, Tooltip, Label,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer
} from 'recharts';
import Skeleton from "../../../../../components/Atomsv2/Skeleton/Skeleton";

const COLORS = {
    alto: 'var(--red-500)',
    medio: 'var(--yellow-500)',
    bajo: 'var(--green-500)',
};

const CenterLabel = ({ viewBox, total }) => {
    const { cx, cy } = viewBox;
    return (
        <>
            <text x={cx} y={cy - 6} textAnchor="middle" dominantBaseline="middle" fill="var(--gray-900)" fontSize={26} fontWeight="bold">
                {total}
            </text>
            <text x={cx} y={cy + 16} textAnchor="middle" dominantBaseline="middle" fill="var(--gray-900)" fontSize={10} letterSpacing={1}>
                OPERADORES
            </text>
        </>
    );
};

const PieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const { name, value, payload: d } = payload[0];
    return (
        <div className="GraficasMF__tooltip">
            <p className="GraficasMF__tooltip-title">{name}</p>
            <p>{value} operadores</p>
            <p className="GraficasMF__tooltip-pct">{d.porcentaje?.toFixed(1)}%</p>
        </div>
    );
};

const BarTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="GraficasMF__tooltip">
            <p className="GraficasMF__tooltip-title">{d.cliente}</p>
            <p>{d.value} de {d.totalCliente} operadores</p>
            <p className="GraficasMF__tooltip-pct">{d.porcentaje?.toFixed(1)}%</p>
        </div>
    );
};

const Graficas = ({ data, loading }) => {
    const total = data?.length || 0;

    const pieData = useMemo(() => {
        if (!data?.length) return [];
        const alto = data.filter(d => d.ID_NIVEL_FATIGA === 1).length;
        const medio = data.filter(d => d.ID_NIVEL_FATIGA === 2).length;
        const bajo = data.filter(d => d.ID_NIVEL_FATIGA === 3).length;
        return [
            { name: 'Riesgo alto', value: alto, porcentaje: (alto / total) * 100, color: COLORS.alto },
            { name: 'Riesgo medio', value: medio, porcentaje: (medio / total) * 100, color: COLORS.medio },
            { name: 'Riesgo bajo', value: bajo, porcentaje: (bajo / total) * 100, color: COLORS.bajo },
        ];
    }, [data, total]);

    const barData = useMemo(() => {
        if (!data?.length) return [];
        const totalesPorCliente = {};
        const altoPorCliente = {};
        data.forEach(d => {
            const key = d.CLIENTE_PRINCIPAL?.trim();
            if (!key) return;
            totalesPorCliente[key] = (totalesPorCliente[key] || 0) + 1;
            if (d.ID_NIVEL_FATIGA === 1) altoPorCliente[key] = (altoPorCliente[key] || 0) + 1;
        });
        return Object.entries(altoPorCliente)
            .map(([cliente, count]) => ({
                cliente,
                value: count,
                totalCliente: totalesPorCliente[cliente] || 0,
                porcentaje: (count / (totalesPorCliente[cliente] || 1)) * 100,
            }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 8);
    }, [data]);

    return (
        <div className="GraficasMF">

            {
                loading ?
                    <Card className={"gridMonitorFatigaGraficas"}>
                        <div className="pie">
                            <Skeleton width='100%' height='328px'></Skeleton>
                        </div>
                        <div className="barras">
                            <Skeleton width='100%' height='328px'></Skeleton>
                        </div>
                    </Card>
                    :
                    <Card className={"gridMonitorFatigaGraficas"}>
                        <div className="pie">
                            <Card>
                                <p className="GraficasMF__titulo">Distribución de riesgo</p>
                                <p className="GraficasMF__subtitulo">% de operadores por nivel de fatiga</p>
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={68}
                                            outerRadius={98}
                                            dataKey="value"
                                            startAngle={90}
                                            endAngle={-270}
                                            strokeWidth={0}
                                        >
                                            {pieData.map((entry, i) => (
                                                <Cell key={i} fill={entry.color} />
                                            ))}
                                            <Label content={<CenterLabel total={total} />} position="center" />
                                        </Pie>
                                        <Tooltip content={<PieTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="GraficasMF__leyenda">
                                    {pieData.map((entry, i) => (
                                        <span key={i} className="GraficasMF__leyenda-item">
                                            <span className="GraficasMF__leyenda-dot" style={{ background: entry.color }} />
                                            {entry.name} · {entry.value}
                                        </span>
                                    ))}
                                </div>
                            </Card>
                        </div>
                        <div className="barras">
                            <Card>
                                <p className="GraficasMF__titulo">Clientes con mayor concentración de riesgo alto</p>
                                <p className="GraficasMF__subtitulo">Operadores en riesgo alto por cliente principal (top 8)</p>
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={barData} layout="vertical" margin={{ top: 4, right: 24, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--gray-900)" />
                                        <XAxis type="number" tick={{ fill: 'var(--gray-900)', fontSize: 12 }} axisLine={false} tickLine={false} />
                                        <YAxis type="category" dataKey="cliente" width={200} tick={{ fill: 'var(--gray-800)', fontSize: 11 }} axisLine={false} tickLine={false} />
                                        <Tooltip content={<BarTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                                        <Bar dataKey="value" fill={COLORS.alto} radius={[0, 4, 4, 0]} barSize={16} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Card>
                        </div>
                    </Card>
            }



        </div>
    );
};

export default Graficas;